/**
 * Display vocabulary shared by the Bexio surfaces.
 *
 * These labels and tone rules lived inside AdminBexioPage. The overview needs
 * the same ones, and two hand-maintained copies of a product's German labels is
 * how "Teilweise" and "Teilweise bezahlt" end up on two screens describing the
 * same status. Moved here rather than copied — AdminBexioPage imports them back.
 *
 * Dates are NOT here on purpose: `utils/datetime.js` is the canonical formatter
 * and it pins Europe/Zurich, which AdminBexioPage's own local helper never did.
 */

/** Semantic tone shared with the Badge components: good | warn | bad | neutral. */

/** Connection status -> German label. */
export const CONNECTION_STATUS_LABEL = {
  active: "Aktiv",
  paused: "Pausiert",
  error: "Fehler",
};

/**
 * Sync `entity_scope` -> German label.
 *
 * Covers maintenance scopes too, because the run HISTORY legitimately lists them —
 * only the "last successful sync" card excludes them, and that happens in the
 * backend. Without an entry here they rendered as the raw English identifier.
 */
export const SCOPE_LABEL = {
  invoices: "Rechnungen",
  contacts: "Kontakte",
  payments: "Zahlungen",
  currency_repair: "Währungsprüfung",
  init: "Initialisierung",
};

/** Sync-run status -> German label (tone comes from `syncStatusTone`). */
export const SYNC_STATUS_LABEL = {
  success: "Erfolg",
  error: "Fehler",
  partial: "Teilweise",
  skipped: "Übersprungen",
  running: "Läuft",
};

/** Bexio invoice status -> German label. */
/**
 * The provider-presence words, in one place because four surfaces say them.
 *
 * "Nicht mehr in Bexio" is what a pill has room for; the long form is what a
 * drawer, a warning row and a tooltip use. Neither says "gelöscht": the state we
 * persist is "a completed inventory looked and did not find it", and a deletion is
 * only the most likely reason for that — never one this system observed.
 */
export const PROVIDER_MISSING_LABEL_SHORT = "Nicht mehr in Bexio";
export const PROVIDER_MISSING_LABEL = "In Bexio nicht mehr vorhanden";

/**
 * The sentence an operator needs when an invoice they can see is missing from every
 * current figure. Three facts, in the order the questions arrive: the row is still
 * here, the provider no longer has it, and that is why the money is gone.
 */
export const PROVIDER_MISSING_EXPLANATION =
  "Diese Rechnung bleibt im lokalen Bexio-Verlauf gespeichert, wurde bei der " +
  "letzten vollständigen Bexio-Prüfung jedoch nicht mehr gefunden und wird " +
  "deshalb nicht als aktuelle Forderung berücksichtigt.";

export const INVOICE_STATUS_LABEL = {
  draft: "Entwurf",
  pending: "ausstehend",
  paid: "bezahlt",
  partial: "teilweise bezahlt",
  canceled: "abgesagt",
  cancelled: "abgesagt",
  unknown: "unbekannt",
};

/** Contact -> tenancy mapping status -> German label. */
export const MAPPING_STATUS_LABEL = {
  unmapped: "Nicht zugeordnet",
  suggested: "Vorschlag vorhanden",
  mapped: "Zugeordnet",
  conflict: "Konflikt",
  not_contact_level_mappable: "Nicht 1:1 zuordenbar",
  invalid: "Ungültig",
  stale: "Quelle prüfen",
};

/**
 * Map a raw enum to its German label.
 *
 * An unknown value is shown raw rather than swallowed: a status the backend
 * starts sending before the frontend knows it should be visible, not invisible.
 */
export function mapLabel(map, value) {
  if (value === null || value === undefined || value === "") return "—";
  return map[value] || value;
}

export function connectionTone(status) {
  if (status === "active") return "good";
  if (status === "error") return "bad";
  if (status === "paused") return "warn";
  return "neutral";
}

export function syncStatusTone(status) {
  if (status === "success") return "good";
  if (status === "error" || status === "partial") return "bad";
  if (status === "running") return "warn";
  return "neutral";
}

/**
 * Mapping status -> semantic tone.
 *
 * `not_contact_level_mappable` is deliberately NOT `bad`. It does not mean broken
 * data or an illegal state — it means the contact cannot be pinned to a single
 * tenancy at contact level, which is a legitimate shape here: a Bexio contact may
 * serve 0..N tenancies while a tenancy has at most one billing contact (migration
 * 141 dropped the contact-side uniqueness precisely to allow that). Red said
 * "error" about a supported situation.
 *
 * It joins `suggested` / `stale` in `warn`: a human should look, nothing is wrong.
 * `neutral` would have been the other candidate and is worse — it is what
 * `unmapped` falls through to, so the two would become indistinguishable and the
 * reader would lose the difference between "nobody assigned this yet" and "this one
 * does not resolve to a single tenancy by nature".
 *
 * `conflict` and `invalid` stay `bad`: a tenancy already bound to another contact,
 * and a mapping whose target no longer resolves, are real defects.
 */
export function mappingTone(status) {
  if (status === "mapped") return "good";
  if (
    status === "suggested" ||
    status === "stale" ||
    status === "not_contact_level_mappable"
  )
    return "warn";
  if (status === "conflict" || status === "invalid") return "bad";
  return "neutral";
}

/**
 * Money, with the currency the backend reported.
 *
 * Currency is a parameter and not assumed to be CHF: `bexio_invoices` mirrors
 * whatever Bexio returned, and the reconciliation surfaces a non-CHF warning
 * precisely because foreign-currency documents occur.
 *
 * OMITTING the argument still means CHF, and that is correct for the callers who
 * do: the reconciliation summary, the expected-vs-invoiced summary and the
 * revenue analytics contract all declare `currency: "CHF"` for their own totals
 * and never carry a per-row code. Do NOT use this for a per-invoice amount —
 * `bexio_invoices.currency` is nullable and `fmtInvoiceMoney` exists for it.
 */
export function fmtMoney(v, currency) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${currency || "CHF"} ${n.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** What the UI says when the provider never identified an invoice's currency. */
export const UNKNOWN_CURRENCY_LABEL = "Unbekannt";
const UNKNOWN_CURRENCY_MARKER = "Währung unbekannt";

/**
 * A PER-INVOICE amount, whose currency the provider may never have confirmed.
 *
 * `bexio_invoices.currency` has been nullable since migration 159, and CURRENCY-2
 * deliberately left provider-missing invoices at NULL rather than inventing a
 * code for them. NULL is a third state: not CHF, not a foreign currency, but
 * "nobody told us". Rendering it as CHF re-asserts exactly the claim migration
 * 159 removed from the database — one layer further out, where no constraint can
 * catch it.
 *
 * So an unknown currency loses the prefix and gains a visible marker instead of
 * borrowing a plausible one. It is deliberately NOT silent: an amount printed
 * bare would read as CHF to any Swiss reader, which is the same untruth without
 * the word.
 *
 * A separate function rather than a change to `fmtMoney`, because the two answer
 * different questions. `fmtMoney(v)` means "this figure is CHF by contract" and
 * roughly thirty call sites rely on that; making the missing argument mean
 * "unknown" would strip a correct CHF from every one of them.
 *
 * `marker: false` is for the shape where repeating it would be noise: a record
 * that already states its currency ADJACENTLY — a drawer with its own "Währung"
 * field, or the second and third amounts of a row whose first amount carries the
 * marker. The reader is told once per record, not once per figure. It is never
 * correct on a figure that stands alone.
 */
export function fmtInvoiceMoney(v, currency, { marker = true } = {}) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const amount = n.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (currency) return `${currency} ${amount}`;
  return marker ? `${amount} · ${UNKNOWN_CURRENCY_MARKER}` : amount;
}

/**
 * An invoice's currency for display — the code, or an explicit unknown marker.
 *
 * Never an empty string: a blank field reads as "not filled in yet" rather than
 * "the provider could not tell us", and the two are different facts.
 */
export function currencyLabel(currency) {
  return currency || UNKNOWN_CURRENCY_LABEL;
}

/**
 * How long ago, in coarse German.
 *
 * Pure arithmetic on a timestamp the page already has — no extra request. Only
 * ever coarser than the absolute time shown beside it, so the two cannot
 * contradict each other. Returns null when there is nothing honest to say
 * (missing, unparseable, or in the future, which would mean clock skew).
 */
export function relativeFromNow(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const seconds = Math.floor((now - t) / 1000);
  if (seconds < 0) return null;
  if (seconds < 60) return "Gerade eben";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Vor ${minutes} ${minutes === 1 ? "Minute" : "Minuten"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Vor ${hours} ${hours === 1 ? "Stunde" : "Stunden"}`;
  const days = Math.floor(hours / 24);
  return `Vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
}

/**
 * Swiss short date, moved verbatim from `AdminBexioPage` (UI-4b) so the page and
 * the extracted invoice mapping modal share one implementation.
 *
 * NOTE this is NOT the same function as the `fmtDate` on the new invoices page,
 * which splits a `YYYY-MM-DD` string instead of going through `Date`. The two
 * agree on date-only input in de-CH but differ on full timestamps, so they were
 * deliberately not merged in this slice — unifying them would change behaviour
 * on a surface this slice is not otherwise touching.
 */
export function fmtDateCH(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("de-CH");
  } catch {
    return "—";
  }
}

/* ── Moved verbatim from `AdminBexioPage` (UI-4b) so the legacy page and the
   extracted mapping modals read one definition. Behaviour unchanged. ── */

export const INVOICE_MAPPING_STATUS_LABEL = {
  mapped: "Zugeordnet",
  unmapped: "Nicht zugeordnet",
  invalid: "Ungültig",
};

export function invoiceMappingTone(s) {
  if (s === "mapped") return "good";
  if (s === "invalid") return "bad";
  return "neutral";
}

export function tenancyPrimaryName(t) {
  const ps = Array.isArray(t?.participants) ? t.participants : [];
  const primary = ps.find((p) => p.role === "primary_tenant") || ps[0];
  const tn = primary?.tenant;
  if (!tn) return null;
  const full = `${tn.first_name || ""} ${tn.last_name || ""}`.trim();
  return full || tn.name || tn.email || null;
}
